import {Controller, useFieldArray, useForm, useWatch} from 'react-hook-form';
import {TextFieldElement} from 'react-hook-form-mui';
import {yupResolver} from '@hookform/resolvers/yup';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import {
  Autocomplete,
  Box,
  Button,
  DialogActions,
  DialogContent,
  IconButton,
  TextField,
  Typography,
} from '@mui/material';
import {isAxiosError} from 'axios';
import {useSnackbar} from 'notistack';

import {useGetPaymentCurrencies} from '@entities/currency';
import {
  useCreateVoucherConfig,
  useGetVoucherConfig,
  useUpdateVoucherConfig,
  VoucherConfigDetails,
} from '@entities/voucher';
import {getErrorMessageFromApiError} from '@shared/api';
import {Dialog, DialogTitle} from '@shared/components';
import {createTranslationDict} from '@shared/helpers';
import {SupportedLanguage} from '@shared/types';
import {voucherConfigFormSchema, VoucherConfigFormValues} from './formSchema';

interface FormProps {
  config?: VoucherConfigDetails;
  onClose: () => void;
}

const nameLanguages = [
  SupportedLanguage.English,
  SupportedLanguage.Spanish,
  SupportedLanguage.Portugal,
  SupportedLanguage.Tagalog,
] as const;

const ConfigForm = ({config, onClose}: FormProps) => {
  const {enqueueSnackbar} = useSnackbar();
  const {
    data: currencies = [],
    isPending: isCurrenciesPending,
    error: currenciesError,
  } = useGetPaymentCurrencies();
  const createMutation = useCreateVoucherConfig();
  const updateMutation = useUpdateVoucherConfig();
  const isPending = createMutation.isPending || updateMutation.isPending;
  const submitLabel = config ? 'Save' : 'Add';
  const {
    control,
    handleSubmit,
    getValues,
    formState: {errors},
  } = useForm<VoucherConfigFormValues>({
    resolver: yupResolver(voucherConfigFormSchema),
    defaultValues: {
      name: createTranslationDict(config?.nameTranslations),
      amounts: config?.amounts ?? [{currency: 'USD', amount: 0.01}],
      ttlDays: config ? (config.ttlDays ?? undefined) : 7,
    },
  });
  const {fields, append, remove} = useFieldArray({control, name: 'amounts'});
  const amounts = useWatch({control, name: 'amounts'});
  const maxCurrencies = new Set(['USD', ...currencies]).size;
  const canAddCurrency =
    !isPending &&
    !isCurrenciesPending &&
    !currenciesError &&
    fields.length < maxCurrencies &&
    currencies.some(currency =>
      amounts.every(item => item.currency !== currency),
    );

  const handleAddCurrency = () => {
    if (!canAddCurrency || getValues('amounts').length >= maxCurrencies) return;
    append({currency: '', amount: 0.01});
  };

  const handleClose = () => {
    if (!isPending) onClose();
  };

  const onSubmit = async (values: VoucherConfigFormValues) => {
    const params = {
      amounts: values.amounts,
      ttlDays: values.ttlDays,
      nameTrans: createTranslationDict(values.name),
    };
    try {
      await (config
        ? updateMutation.mutateAsync({id: config.id, ...params})
        : createMutation.mutateAsync(params));
      enqueueSnackbar(config ? 'Voucher updated' : 'Voucher created', {
        variant: 'success',
      });
      onClose();
    } catch (error) {
      const message = isAxiosError(error)
        ? getErrorMessageFromApiError(error)
        : '';
      enqueueSnackbar(`Could not save voucher. ${message}`, {
        variant: 'error',
      });
    }
  };

  return (
    <Dialog open onClose={handleClose}>
      <form onSubmit={handleSubmit(onSubmit)} noValidate>
        <DialogTitle
          title={config ? 'Edit voucher' : 'Add voucher'}
          onClose={isPending ? undefined : handleClose}
        />
        <DialogContent
          sx={{
            width: 600,
            maxWidth: '90vw',
            display: 'grid',
            gap: 4,
            '&&': {pt: 4},
            '& .MuiFormHelperText-root': {
              position: 'static',
              transform: 'none',
              mt: 1,
            },
            '& .MuiFormHelperText-root:not(.Mui-error)': {
              color: 'text.secondary !important',
            },
          }}
        >
          <Box display="grid" gap={2}>
            <Typography>Name</Typography>
            {nameLanguages.map(lang => (
              <Box key={lang} display="flex" alignItems="center" gap={2}>
                <Typography
                  component="label"
                  htmlFor={`voucher-name-${lang}`}
                  sx={{minWidth: '10ch'}}
                >
                  {lang}:
                </Typography>
                <TextFieldElement
                  id={`voucher-name-${lang}`}
                  name={`name.${lang}`}
                  control={control}
                  disabled={isPending}
                  fullWidth
                />
              </Box>
            ))}
          </Box>
          {fields.map((item, index) => (
            <Box key={item.id} display="flex" alignItems="flex-start" gap={2}>
              {item.currency === 'USD' ? (
                <TextFieldElement
                  name={`amounts.${index}.currency`}
                  control={control}
                  label={`Currency ${index + 1}`}
                  InputProps={{readOnly: true}}
                  error={!!currenciesError}
                  helperText={
                    currenciesError
                      ? 'Could not load currencies.'
                      : isCurrenciesPending && 'Loading currencies…'
                  }
                  required
                  sx={{flex: 1}}
                />
              ) : (
                <Controller
                  name={`amounts.${index}.currency`}
                  control={control}
                  render={({field, fieldState}) => (
                    <Autocomplete
                      sx={{flex: 1}}
                      options={[
                        ...new Set([...currencies, field.value]),
                      ].filter(
                        currency =>
                          currency !== '' &&
                          (currency === field.value ||
                            amounts.every(
                              value => value.currency !== currency,
                            )),
                      )}
                      value={field.value || null}
                      onChange={(_, value) => field.onChange(value ?? '')}
                      onBlur={field.onBlur}
                      loading={isCurrenciesPending}
                      disabled={
                        isPending || isCurrenciesPending || !!currenciesError
                      }
                      renderInput={params => (
                        <TextField
                          {...params}
                          inputRef={field.ref}
                          label={`Currency ${index + 1}`}
                          required
                          error={!!fieldState.error || !!currenciesError}
                          helperText={
                            currenciesError
                              ? 'Could not load currencies.'
                              : (fieldState.error?.message ??
                                (isCurrenciesPending
                                  ? 'Loading currencies…'
                                  : undefined))
                          }
                        />
                      )}
                    />
                  )}
                />
              )}
              <TextFieldElement
                name={`amounts.${index}.amount`}
                control={control}
                label={`Amount ${index + 1}`}
                type="number"
                inputProps={{min: 0.01, step: 0.01}}
                disabled={isPending}
                required
                sx={{flex: 1}}
              />
              {item.currency === 'USD' ? (
                <Box width={40} flexShrink={0} />
              ) : (
                <IconButton
                  aria-label={`Remove currency ${index + 1}`}
                  disabled={isPending}
                  onClick={() => remove(index)}
                >
                  <DeleteOutlineIcon />
                </IconButton>
              )}
            </Box>
          ))}
          {(errors.amounts?.root?.message ?? errors.amounts?.message) && (
            <Typography color="error">
              {errors.amounts?.root?.message ?? errors.amounts?.message}
            </Typography>
          )}
          <Button
            variant="outlined"
            onClick={handleAddCurrency}
            disabled={!canAddCurrency}
          >
            Add currency (optional)
          </Button>
          <TextFieldElement
            name="ttlDays"
            control={control}
            label="Lifetime (days)"
            type="number"
            inputProps={{min: 1, step: 1}}
            disabled={isPending}
            required
          />
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={handleClose} disabled={isPending}>
            Cancel
          </Button>
          <Button
            type="submit"
            disabled={
              isPending ||
              isCurrenciesPending ||
              !!currenciesError ||
              currencies.length === 0
            }
          >
            {isPending ? 'Saving…' : submitLabel}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

interface Props {
  configId?: string;
  onClose: () => void;
}

export const ConfigDialog = ({configId, onClose}: Props) => {
  const {data, isPending, error, refetch} = useGetVoucherConfig(
    configId ?? '',
    {
      enabled: !!configId,
    },
  );

  if (!configId || data) {
    return <ConfigForm config={data} onClose={onClose} />;
  }

  return (
    <Dialog open onClose={onClose}>
      <DialogTitle title="Edit voucher" onClose={onClose} />
      <DialogContent sx={{width: 600, maxWidth: '90vw'}}>
        <Typography>
          {isPending ? 'Loading voucher…' : 'Could not load voucher.'}
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" onClick={onClose}>
          Cancel
        </Button>
        {error && <Button onClick={() => void refetch()}>Retry</Button>}
      </DialogActions>
    </Dialog>
  );
};
